import { useCallback, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import {
  getPartsTree,
  getOfflinePartsTree,
} from "../../repositories/CasterMoldRepository";
import {
  STRAND_CONTAINER_IDS,
  transformAPIToTreeData,
  type PartsTreeRowAPI,
} from "../../models";
/**
 * Query hook for the Caster Mold parts tree.
 *
 * Splits into 6 independent React Query caches:
 *   - 5× strand queries  ["caster-mold-strand", <strandId>]
 *   - 1× offline query   ["caster-mold-offline"]
 *
 * Each resolves independently so the UI updates incrementally
 * (one strand returning doesn't block the others).
 */
export const useCasterMoldPartsTreeQuery = () => {
  const queryClient = useQueryClient();

  const queries = useQueries({
    queries: [
      ...STRAND_CONTAINER_IDS.map((strandId) => ({
        queryKey: ["caster-mold-strand", strandId] as const,
        queryFn: async (): Promise<PartsTreeRowAPI[]> => {
          const resp = await getPartsTree(strandId);
          if (!resp.ok) return [];
          return get(resp, "data.tables[0].rows", []);
        },
        refetchInterval: 10_000,
      })),
      {
        queryKey: ["caster-mold-offline"] as const,
        queryFn: async (): Promise<PartsTreeRowAPI[]> => {
          const resp = await getOfflinePartsTree();
          if (!resp.ok) return [];
          return get(resp, "data.tables[0].rows", []);
        },
        // No polling — invalidated via SignalR by useRealtimeInvalidation
      },
    ],
  });

  // Each queries[i].data is a stable reference from React Query —
  // only changes when that specific query refetches with new data.
  const data = useMemo(() => {
    const allRows = queries.flatMap((q) => q.data ?? []);
    if (allRows.length === 0) return [];
    return transformAPIToTreeData(allRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queries[0].data,
    queries[1].data,
    queries[2].data,
    queries[3].data,
    queries[4].data,
    queries[5].data,
  ]);

  return {
    data,
    isLoading: queries.some((q) => q.isLoading),
    refetch: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["caster-mold-strand"] });
      queryClient.invalidateQueries({ queryKey: ["caster-mold-offline"] });
      queryClient.invalidateQueries({
        queryKey: ["caster-mold-strand-history"],
      });
      queryClient.invalidateQueries({ queryKey: ["caster-mold-details"] });
    }, [queryClient]),
  };
};
